import type { WalletContextState } from "@solana/wallet-adapter-react";
import { useWallet as useWalletInternal } from "@solana/wallet-adapter-react";
import type { PublicKey } from "@solana/web3.js";
import type { FC } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import { isPublicKeyWalletAdapter } from "../utils/wallet/providers";

const DEFAULT_ENRICHED_CONTEXT = { ...({} as WalletContextState), watching: false, connected: false };
type EnrichedWalletContextState = typeof DEFAULT_ENRICHED_CONTEXT;

const EnrichedWalletContext = createContext<EnrichedWalletContextState>(DEFAULT_ENRICHED_CONTEXT);

export const EnrichedWalletProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const walletContextInternal = useWalletInternal();
  const { connected, wallet, publicKey: publicKeyInternal } = walletContextInternal;

  const [walletContext, setWalletContext] = useState<WalletContextState>({} as WalletContextState);
  const [publicKey, setPublicKey] = useState<PublicKey | null>(null);
  const [watching, setWatching] = useState(false);

  const adapterName = useRef(wallet?.adapter.name);
  const isNewAdapter = useRef(false);

  // Order of prop updates in Solana Labs WalletProvider context:
  // - Wallet is selected, adapter name is updated.
  // - Connect event is then triggered, which updates publicKeyInternal.
  // Some adapters trigger a connect event when switching wallets in the extension.
  // We block the publicKey prop from updating in that case with the useEffect logic below.
  useEffect(() => {
    isNewAdapter.current = true;
    adapterName.current = wallet?.adapter.name;
    setPublicKey(null);
  }, [wallet?.adapter.name]);

  // When publicKeyInternal changes, publicKey is updated only if adapter name has also changed.
  // Behaviour is ignored when switching from one watched wallet to another.
  useEffect(() => {
    if ((isNewAdapter.current && publicKeyInternal !== null) || adapterName.current === "PublicKey") {
      setPublicKey(publicKeyInternal);
      isNewAdapter.current = false;
    } else if (publicKeyInternal === null) {
      setPublicKey(null);
    }
  }, [publicKeyInternal]);

  // Pass the rest of useWallet state through intermediate effects so
  // entire context value only updates on the same render cycle as publicKey.
  useEffect(() => {
    setWalletContext(walletContextInternal);
  }, [walletContextInternal]);

  useEffect(() => {
    setWatching(isPublicKeyWalletAdapter(wallet) && connected);
  }, [wallet, connected]);

  return (
    <EnrichedWalletContext.Provider value={{ ...walletContext, watching, publicKey }}>
      {children}
    </EnrichedWalletContext.Provider>
  );
};

export const useEnrichedWallet = () => useContext(EnrichedWalletContext);
